/**
 * Speech service for recording and playing audio
 * Wraps the audio service with conversation-specific logic
 */

import { AudioRecorder, AudioPlayer, RecordingResult } from './audio'

export class SpeechService {
  private recorder: AudioRecorder
  private player: AudioPlayer

  constructor() {
    this.recorder = new AudioRecorder()
    this.player = new AudioPlayer()
  }

  /**
   * Initialize microphone
   */
  async requestMicrophonePermission(): Promise<void> {
    await this.recorder.initialize()
  }

  /**
   * Start recording user speech
   */
  async startRecording(): Promise<void> {
    await this.recorder.startRecording({
      mimeType: 'audio/webm;codecs=opus',
      audioBitsPerSecond: 128000,
    })
  }

  /**
   * Stop recording and get audio data
   */
  async stopRecording(): Promise<RecordingResult> {
    return await this.recorder.stopRecording()
  }

  /**
   * Cancel current recording
   */
  cancelRecording(): void {
    this.recorder.cancelRecording()
  }

  /**
   * Play AI response audio
   */
  async playAudio(audioUrl: string): Promise<void> {
    // Handle base64 encoded audio
    if (audioUrl.startsWith('data:audio/')) {
      await this.player.play(audioUrl)
      return
    }

    try {
      const response = await fetch(audioUrl, {
        cache: 'force-cache',
      })
      if (!response.ok) {
        throw new Error(`Failed to load audio (${response.status})`)
      }
      const blob = await response.blob()
      await this.player.play(blob)
    } catch (error) {
      console.error('[speech] Failed to fetch audio, falling back to direct URL', error)
      await this.player.play(audioUrl)
    }
  }

  /**
   * Stop audio playback
   */
  stopPlayback(): void {
    this.player.stop()
  }

  /**
   * Adjust AI playback speed
   */
  setPlaybackRate(rate: number): void {
    this.player.setPlaybackRate(rate)
  }

  /**
   * Speak text using browser TTS (for voice command feedback)
   */
  async speak(text: string, lang: string = 'en-US'): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!window.speechSynthesis) {
        reject(new Error('Speech synthesis not supported'))
        return
      }

      const utterance = new SpeechSynthesisUtterance(text)
      utterance.lang = lang
      utterance.rate = 1.0
      utterance.pitch = 1.0
      utterance.volume = 1.0

      utterance.onend = () => resolve()
      utterance.onerror = (event) => reject(event)

      window.speechSynthesis.speak(utterance)
    })
  }

  /**
   * Check if currently recording
   */
  get isRecording(): boolean {
    return this.recorder.isRecording
  }

  /**
   * Check if audio is playing
   */
  get isPlaying(): boolean {
    return this.player.isPlaying
  }

  /**
   * Get current recording duration
   */
  get recordingDuration(): number {
    return this.recorder.currentDuration
  }

  /**
   * Clean up resources
   */
  cleanup(): void {
    this.recorder.cleanup()
    this.player.stop()
  }
}

// Export singleton instance
export const speechService = new SpeechService()

export default speechService
